import { CommonModule } from '@angular/common';
import { Component, ElementRef, AfterViewInit, OnDestroy, ViewChild, ViewEncapsulation } from '@angular/core';
import { OverlayContainer } from '@angular/cdk/overlay';
import { ToastService } from '../../services/toast.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-toast-container',
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule],
  template: `
    <div #toastWrapper class="toast-container-wrapper" popover="manual">
      @for (toast of toastService.toasts$ | async; track toast.id) {
        <div class="toast-item" [ngClass]="toast.panelClass"
          (mouseenter)="toastService.pause(toast.id)"
          (mouseleave)="toastService.resume(toast.id)">
          <span class="toast-icon">
              <i [ngClass]="getIconClass(toast.panelClass)"></i>
          </span>
          <span class="toast-message">{{ toast.message }}</span>
          <button class="toast-close" type="button" (click)="dismiss(toast.id)">&#x2715;</button>
        </div>
      }
    </div>
  `,
  styles: [`
    .toast-container-wrapper {
      position: fixed;
      bottom: 25px;
      right: 20px;
      z-index: 2147483647;
      pointer-events: all;
      display: flex;
      flex-direction: column;
      gap: 8px;
      max-width: 300px;
      width: 300px;
      max-height: calc(100vh - 40px);
      overflow: hidden;
      border: none;
      padding: 0;
      margin: 0;
      background: transparent;
      color: inherit;
      inset: unset;
    }
    .toast-item {
      display: flex;
      align-items: center;
      padding: 12px 14px;
      border-radius: 4px;
      color: #fff;
      font-size: 14px;
      font-weight: 500;
      box-shadow: 0 2px 8px rgba(0,0,0,.25);
      animation: slideIn 0.3s ease;
    }
    @keyframes slideIn {
      from { transform: translateX(110%); opacity: 0; }
      to   { transform: translateX(0);    opacity: 1; }
    }
    .toast-icon {
      flex-shrink: 0;
      margin-right: 10px;
      font-size: 16px;
      line-height: 1.3;
    }
    .toast-shield-svg {
      width: 20px;
      height: 20px;
      display: block;
    }
    .toast-message {
      flex: 1;
      word-break: break-word;
      line-height: 1.4;
    }
    .toast-close {
      flex-shrink: 0;
      background: none;
      border: none;
      color: #fff;
      margin-left: 10px;
      cursor: pointer;
      font-size: 11px;
      line-height: 1;
      padding: 0;
      opacity: 0.85;
    }
    .toast-close:hover { opacity: 1; }
    .toast-success { background-color: #61c250; }
    .toast-error   { background-color: #b00020; }
    .toast-warn    { background-color: #f5a623; }
    .toast-info    { background-color: #369eff; }
    .toast-error .toast-icon i { display: none; }
    .toast-error .toast-icon::after {
      content: '';
      display: inline-block;
      width: 18px;
      height: 18px;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='white'%3E%3Cpath d='M12,1L3,5V11C3,16.55 6.84,21.74 12,23C17.16,21.74 21,16.55 21,11V5L12,1M11,7H13V13H11V7M11,15H13V17H11V15Z'/%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-size: contain;
    }
  `]
})
export class ToastContainerComponent implements AfterViewInit, OnDestroy {
  @ViewChild('toastWrapper') toastWrapperRef!: ElementRef<HTMLElement>;
  private toastSub?: Subscription;

  constructor(
    public toastService: ToastService,
    private el: ElementRef,
    private overlayContainer: OverlayContainer
  ) {}

  ngAfterViewInit() {
    this.overlayContainer.getContainerElement().appendChild(this.el.nativeElement);

    const wrapper = this.toastWrapperRef?.nativeElement as any;
    if (wrapper && typeof wrapper.showPopover === 'function') {
      const applyPosition = () => {
        Object.assign(wrapper.style, {
          position: 'fixed',
          inset: 'unset',
          top: 'auto',
          left: 'auto',
          bottom: '25px',
          right: '20px',
          margin: '0',
          border: 'none',
          padding: '0',
          background: 'transparent',
          width: '300px',
          maxWidth: '300px',
        });
      };

      this.toastSub = this.toastService.toasts$.subscribe(toasts => {
        if (toasts.length > 0) {
          if (wrapper.matches(':popover-open')) {
            applyPosition();
          } else {
            wrapper.showPopover();
            applyPosition();
          }
        } else {
          try { wrapper.hidePopover(); } catch { /* ignore: already hidden */ }
        }
      });
    }
  }

  ngOnDestroy() {
    this.toastSub?.unsubscribe();
  }

  getIconClass(panelClass: string): string {
    switch (panelClass) {
      case 'toast-success': return 'fas fa-check-circle';
      case 'toast-error':   return '';
      case 'toast-warn':    return 'fas fa-exclamation-triangle';
      case 'toast-info':    return 'fas fa-info-circle';
      default:              return 'fas fa-info-circle';
    }
  }

  dismiss(id: number) {
    this.toastService.dismiss(id);
  }
}
